import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOpenEstimates, updateInvoice } from '../services/invoices';
import { sendEstimateEmail } from '../services/email';
import { getLetterTemplate, getDefaultLetterTemplates } from '../services/letterTemplates';
import { renderTemplate } from '../utils/templateRenderer';
import { assembleDocument } from '../utils/assembleDocument';
import './OpenEstimates.css';

export default function OpenEstimates() {
  const { user } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printEstimate, setPrintEstimate] = useState(null);
  const [letterTemplate, setLetterTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const navigate = useNavigate();

  const loadEstimates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOpenEstimates();
      setEstimates(data);
    } catch (error) {
      console.error('Error loading open estimates:', error);
      alert('Failed to load open estimates: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEstimates();
  }, [loadEstimates]);

  // Listen for estimate finalized events
  useEffect(() => {
    const handleEstimateFinalized = () => {
      loadEstimates();
    };
    
    window.addEventListener('estimateFinalized', handleEstimateFinalized);
    return () => {
      window.removeEventListener('estimateFinalized', handleEstimateFinalized);
    };
  }, [loadEstimates]);

  const handleViewClient = (clientId, estimateId = null) => {
    navigate(`/clients/${clientId}`, { state: { activeTab: 'history', invoiceId: estimateId } });
  };

  const handlePrint = (estimate) => {
    setPrintEstimate(estimate);
  };

  const handleEmail = async (estimate) => {
    try {
      const client = estimate.clients;
      if (!client || !client.email) {
        alert('Client email is required to send email');
        return;
      }

      // Show info message
      alert(`A PDF estimate will be downloaded. After it downloads, your email client will open. Please attach the downloaded PDF file before sending the email.`);

      await sendEstimateEmail(estimate, client);
    } catch (error) {
      alert('Failed to send email: ' + error.message);
    }
  };

  const handleHideTotalsToggle = async (estimateId, currentValue) => {
    try {
      const newValue = !currentValue;
      await updateInvoice(estimateId, { hide_totals: newValue });
      await loadEstimates();
    } catch (error) {
      alert('Failed to update hide totals status: ' + error.message);
    }
  };

  const parseItems = (items) => {
    try {
      return typeof items === 'string' ? JSON.parse(items) : items;
    } catch {
      return [];
    }
  };

  // Load letter template for print view
  useEffect(() => {
    const loadTemplate = async () => {
      if (!printEstimate) return;
      
      try {
        setLoadingTemplate(true);
        const template = await getLetterTemplate('estimate');
        setLetterTemplate(template);
      } catch (error) {
        console.log('Using default letter template:', error);
        const defaults = getDefaultLetterTemplates();
        setLetterTemplate(defaults.estimate);
      } finally {
        setLoadingTemplate(false);
      }
    };
    
    loadTemplate();
  }, [printEstimate]);

  // Open print dialog only after template has loaded and content has rendered
  useEffect(() => {
    if (!printEstimate || !letterTemplate || loadingTemplate) return;
    const timeoutId = setTimeout(() => window.print(), 150);
    const onAfterPrint = () => {
      setPrintEstimate(null);
      window.removeEventListener('afterprint', onAfterPrint);
    };
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [printEstimate, letterTemplate, loadingTemplate]);

  // Print view component - uses template from Settings
  const PrintView = ({ estimate }) => {
    if (loadingTemplate || !letterTemplate) {
      return <div className="print-view">Loading template...</div>;
    }
    
    const items = parseItems(estimate.items);
    const client = estimate.clients;
    const clientCityStateZip = [client?.city, client?.state, client?.zip].filter(Boolean).join(', ') || '';
    
    // Prepare template data
    const templateData = {
      document_type: 'ESTIMATE',
      client: {
        name: client?.name || 'Client',
        address: client?.address || '',
        city_state_zip: clientCityStateZip,
        email: client?.email || '',
        phone: client?.phone || '',
      },
      invoice: {
        number: estimate.invoice_number || estimate.id || '',
        date: estimate.date ? new Date(estimate.date).toLocaleDateString() : '',
        due_date: estimate.due_date ? new Date(estimate.due_date).toLocaleDateString() : '',
        document_type: 'ESTIMATE',
        subtotal: `$${parseFloat(estimate.subtotal || 0).toFixed(2)}`,
        tax: `$${parseFloat(estimate.tax_amount || 0).toFixed(2)}`,
        tax_amount: `$${parseFloat(estimate.tax_amount || 0).toFixed(2)}`,
        total: `$${parseFloat(estimate.total || 0).toFixed(2)}`,
        tax_rate: estimate.tax_rate || 0,
        taxable_subtotal: estimate.taxable_subtotal || 0,
        non_taxable_subtotal: estimate.non_taxable_subtotal || 0,
        line_items: items,
        items_table: items,
        notes: estimate.notes || '',
      },
      date: estimate.date ? new Date(estimate.date).toLocaleDateString() : '',
      due_date: estimate.due_date ? new Date(estimate.due_date).toLocaleDateString() : '',
      facility: {
        name: 'Menlo Atherton Glass',
        address: 'PO Box 5214, Redwood City CA 94063',
      },
      user: {
        name: user?.user_metadata?.full_name || user?.email || '',
      },
      notes_section: estimate.notes || '',
      tax_rate: estimate.tax_rate || 0,
    };
    
    // UNIFIED PIPELINE: Assemble document then render
    // Step 1: Assemble document structure
    const assembledHTML = assembleDocument({
      header: letterTemplate.header || '',
      body: letterTemplate.body || '',
      footer: letterTemplate.footer || ''
    });
    
    // Step 2: Render with real data using unified renderTemplate
    const templateContent = renderTemplate(assembledHTML, templateData, estimate.hide_totals);
    
    return (
      <div className="print-view">
        <div 
          className="print-template-content"
          dangerouslySetInnerHTML={{ __html: templateContent }}
        />
      </div>
    );
  };

  if (printEstimate) {
    return createPortal(<PrintView estimate={printEstimate} />, document.body);
  }

  if (loading) {
    return (
      <div className="open-estimates-page">
        <div className="loading-state">Loading open estimates...</div>
      </div>
    );
  }

  return (
    <div className="open-estimates-page">
      <div className="page-header">
        <h1>Open Estimates</h1>
        <div className="header-stats">
          <span className="stat-badge">
            {estimates.length} {estimates.length === 1 ? 'Estimate' : 'Estimates'}
          </span>
        </div>
      </div>

      {estimates.length === 0 ? (
        <div className="empty-state">
          <p>✅ No open estimates</p>
          <p className="empty-subtitle">All estimates have been finalized or there are no estimates yet.</p>
        </div>
      ) : (
        <div className="estimates-list">
          {estimates.map((estimate) => {
            const items = parseItems(estimate.items);
            const client = estimate.clients;
            
            return (
              <div key={estimate.id} className="estimate-card">
                <div className="estimate-header">
                  <div className="estimate-info">
                    <h3>
                      {estimate.invoice_number || `Estimate #${estimate.id}`}
                      <span className="estimate-badge">Estimate</span>
                    </h3>
                    <div className="estimate-meta">
                      <span className="client-link" onClick={() => handleViewClient(estimate.client_id)}>
                        Client: {client?.name || 'Unknown Client'}
                      </span>
                      <span>Date: {new Date(estimate.date).toLocaleDateString()}</span>
                      {estimate.created_by && (
                        <span className="creator-badge">
                          Created by: {estimate.created_by_user?.email || 'User ' + estimate.created_by.substring(0, 8)}
                        </span>
                      )}
                      <span className={`status-badge ${estimate.status}`}>
                        {estimate.status}
                      </span>
                    </div>
                  </div>
                  <div className="estimate-actions">
                    <span className="estimate-total">${parseFloat(estimate.total || 0).toFixed(2)}</span>
                    <div className="action-buttons">
                      <button
                        className="btn-action"
                        onClick={() => handlePrint(estimate)}
                        title="Print"
                      >
                        Print
                      </button>
                      <button
                        className="btn-action"
                        onClick={() => handleEmail(estimate)}
                        title="Email"
                        disabled={!estimate.clients?.email}
                      >
                        Email
                      </button>
                      <button
                        className="btn-view-client"
                        onClick={() => handleViewClient(estimate.client_id, estimate.id)}
                        title="View Estimate"
                      >
                        View Estimate
                      </button>
                      <label className="hide-totals-checkbox-label">
                        <input
                          type="checkbox"
                          checked={estimate.hide_totals || false}
                          onChange={() => handleHideTotalsToggle(estimate.id, estimate.hide_totals)}
                        />
                        <span>Hide Totals</span>
                      </label>
                    </div>
                  </div>
                </div>

                {items.length > 0 && (
                  <div className="estimate-items">
                    <table>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit Price</th>
                          <th>Taxable</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => {
                          if (item.isSeparator) {
                            const prevSepIndex = items.slice(0, index).findLastIndex((x) => x.isSeparator);
                            const sectionStart = prevSepIndex >= 0 ? prevSepIndex + 1 : 0;
                            const sectionItems = items.slice(sectionStart, index).filter((i) => !i.isSeparator);
                            const sectionSum = sectionItems.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                            const sectionTaxableSum = sectionItems
                              .filter((i) => i.taxable !== false)
                              .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                            const includeTax = estimate.include_tax !== false;
                            const taxRate = parseFloat(estimate.tax_rate || 0);
                            const hasSectionTax = includeTax && taxRate > 0 && sectionTaxableSum > 0;
                            const sectionTax = hasSectionTax ? sectionTaxableSum * taxRate / 100 : 0;
                            const sectionTotalWithTax = sectionSum + sectionTax;
                            return (
                              <React.Fragment key={index}>
                                {item.showSubtotal && sectionSum > 0 && (
                                  <>
                                    {hasSectionTax && (
                                      <>
                                        <tr className="item-section-subtotal-row">
                                          <td colSpan={4} className="section-subtotal-label">Section Subtotal</td>
                                          <td className="total-cell section-subtotal-value">${sectionSum.toFixed(2)}</td>
                                        </tr>
                                        <tr className="item-section-tax-row">
                                          <td colSpan={4} className="section-subtotal-label">Section Tax ({taxRate}%)</td>
                                          <td className="total-cell section-subtotal-value">${sectionTax.toFixed(2)}</td>
                                        </tr>
                                      </>
                                    )}
                                    <tr className="item-section-subtotal-row">
                                      <td colSpan={4} className="section-subtotal-label">Section Total</td>
                                      <td className="total-cell section-subtotal-value">${sectionTotalWithTax.toFixed(2)}</td>
                                    </tr>
                                  </>
                                )}
                                <tr className="item-separator-row">
                                  <td colSpan="5" className="item-separator">
                                    <div className="separator-line"></div>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          }
                          return (
                            <tr key={index}>
                              <td>{item.description}</td>
                              <td>{item.quantity}</td>
                              <td>${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                              <td>{item.taxable !== false ? 'Yes' : 'No'}</td>
                              <td>${parseFloat(item.total || 0).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {!estimate.hide_totals && (
                  <div className="estimate-summary">
                    <div className="summary-row">
                      <span>Subtotal:</span>
                      <span>${parseFloat(estimate.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {parseFloat(estimate.taxable_subtotal || 0) > 0 && (
                      <div className="summary-row">
                        <span>Taxable Amount:</span>
                        <span>${parseFloat(estimate.taxable_subtotal || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {parseFloat(estimate.non_taxable_subtotal || 0) > 0 && (
                      <div className="summary-row">
                        <span>Non-Taxable Amount:</span>
                        <span>${parseFloat(estimate.non_taxable_subtotal || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {parseFloat(estimate.tax_rate || 0) > 0 && (
                      <div className="summary-row">
                        <span>Tax ({estimate.tax_rate}%):</span>
                        <span>${parseFloat(estimate.tax_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>Total:</span>
                      <span>${parseFloat(estimate.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {estimate.notes && (
                  <div className="estimate-notes">
                    <strong>Notes:</strong> {estimate.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
